/**
 * Pure helpers for the inline insert menu's typed-query mode: flatten the
 * (possibly nested, possibly async) menu items into insertable leaves and
 * filter/rank them against the query the user typed after "/".
 */
import { type MatchInput } from "..";

interface Icon {
    name: string;
    library?: string;
}

/**
 * Structural view on InputMenuItemState (and its children) — everything the
 * flattener needs, without the Omit-chain of the real menu item types.
 */
export interface FlattenableItem {
    name: string;
    label: string;
    icon?: Icon;
    flatGroup?: string;
    disabled?: boolean;
    run?: (event: Event, input: MatchInput) => void;
    items?: FlattenableItem[];
    getItems?: () => Promise<FlattenableItem[] | undefined>;
}

export interface FlatMenuItem {
    key: string;
    label: string;
    // Human type tag shown next to the label ("Domain", "Product", ...).
    group: string;
    icon?: Icon;
    run: (event: Event, input: MatchInput) => void;
}

/**
 * Flatten menu items into a list of runnable leaves.
 *
 * Dropdowns that carry their own `run` (e.g. a domain that also has product
 * children) are emitted as leaves AND recursed into. Disabled items are
 * skipped. Leaves inherit the icon and group tag of their parent dropdown
 * when they have none themselves.
 */
export const flattenInlineMenuItems = async (
    items: FlattenableItem[] | undefined,
    parent?: FlattenableItem
): Promise<FlatMenuItem[]> => {
    if (!items?.length) {
        return [];
    }
    const result: FlatMenuItem[] = [];
    for (const item of items) {
        if (!item) {
            continue;
        }
        const group =
            item.flatGroup ??
            (parent ? (parent.flatGroup ?? parent.label ?? "") : "");
        if (item.run && !item.disabled) {
            result.push({
                key: `${group}:${item.name}`,
                label: item.label,
                group,
                icon: item.icon ?? parent?.icon,
                run: item.run,
            });
        }
        const children =
            item.items ?? (item.getItems ? await item.getItems() : undefined);
        if (children?.length) {
            result.push(...(await flattenInlineMenuItems(children, item)));
        }
    }
    return result;
};

/**
 * Filter and rank flat items against the typed query: label prefix matches
 * first, then label substring matches, then group-tag matches (so "/table"
 * still surfaces the "n columns" leaves of the Insert Table dropdown).
 */
export const filterFlatItems = (
    items: FlatMenuItem[],
    query: string
): FlatMenuItem[] => {
    const q = query.toLowerCase();
    const prefix: FlatMenuItem[] = [];
    const substring: FlatMenuItem[] = [];
    const groupMatch: FlatMenuItem[] = [];
    for (const item of items) {
        const label = item.label.toLowerCase();
        if (label.startsWith(q)) {
            prefix.push(item);
        } else if (label.includes(q)) {
            substring.push(item);
        } else if (item.group.toLowerCase().includes(q)) {
            groupMatch.push(item);
        }
    }
    return [...prefix, ...substring, ...groupMatch];
};
